"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import type { InplayStatus, LiveClock, LivePlay } from "@/lib/odds-screen/live/types";
import { PAUSE_LABELS, type SetCell, type SideLive } from "./labels";
import { KICKOFF_TIME, clockSeconds, clockText, periodLabel } from "./time";

function useClockSeconds(clock: LiveClock | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!clock?.running) return;
    setNow(Date.now());
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [clock]);
  return clock === null ? null : clockSeconds(clock, now);
}

const SET_TONE: Record<SetCell["state"], string> = {
  won: "font-semibold text-foreground",
  lost: "text-zinc-500",
  current: "rounded bg-white/[0.06] font-semibold text-foreground",
};

export function PeriodStrip({ live, className }: { live: SideLive | null; className?: string }) {
  if (!live?.periods) return null;
  return (
    <span data-testid="period-strip" className={cn("data-num flex flex-shrink-0 gap-1 text-[12px] tabular-nums", className)}>
      {live.periods.map((cell, i) => <span key={i} data-period={cell.current ? "current" : "played"} className={cn("min-w-3.5 text-center", cell.current ? SET_TONE.current : "text-zinc-400")}>{cell.points}</span>)}
    </span>
  );
}

export function SideScore({ live, className, inlinePeriods }: { live: SideLive | null; className?: string; inlinePeriods: boolean }) {
  if (live === null) return null;
  const inline = inlinePeriods && live.periods !== null;
  const column = cn("data-num flex flex-shrink-0 justify-end tabular-nums", !inline && "ml-auto", className);
  return (
    <>
      {inline && <PeriodStrip live={live} className="ml-auto leading-5" />}
      {live.sets ? (
        <span data-testid="set-strip" className={cn(column, "gap-0.5 text-[12px] leading-5")}>
          {live.sets.map((set, i) => <span key={i} data-set={set.state} className={cn("w-3.5 text-center", SET_TONE[set.state])}>{set.games}</span>)}
        </span>
      ) : (
        <span data-testid={live.score === null ? undefined : "live-score"} className={cn(column, "text-[13px] font-semibold", live.trailing ? "text-zinc-400" : "text-foreground")}>{live.score}</span>
      )}
      <span data-testid={live.serving ? "serve" : "no-serve"} aria-hidden="true" className={cn("h-1.5 w-1.5 flex-shrink-0 rounded-full", live.serving && "bg-primary ring-2 ring-primary/[0.14]")} />
    </>
  );
}

export function hasPlayOrPeriods(live: InplayStatus | undefined): boolean {
  return live !== undefined && (live.plays.length > 0 || live.periods !== null);
}

export function LatestPlay({ play, className }: { play: LivePlay | null; className?: string }) {
  if (play === null) return null;
  return <span data-testid="latest-play" title={play.text} className={cn("block min-w-0 truncate text-[11px] leading-4 text-zinc-500", className)}>{play.text}</span>;
}

export function sideTone(live: SideLive | null): string {
  return live?.trailing ? "text-zinc-400" : "text-foreground";
}

export function LiveState({ live, compact = false, className }: { live: InplayStatus; compact?: boolean; className?: string }) {
  const seconds = useClockSeconds(live.clock);
  const { period, pause } = live;
  const stopped = live.paused || !!pause;
  const stoppage = live.paused ? "Paused" : pause && !(pause.kind === "period_break" && period?.break) ? PAUSE_LABELS[pause.kind] : null;
  const label = period !== null ? periodLabel(period, compact) : stoppage ?? "Live";
  const points = live.sets && live.sets.homePoints !== "" ? `${live.sets.awayPoints}-${live.sets.homePoints}` : null;
  const clock = period !== null && !period.break && seconds !== null ? clockText(seconds) : null;
  const running = points ?? clock;
  const frozen = live.paused || period?.break ? null : points ?? (live.clock?.running ? null : clock);
  const detail = !stopped ? running : period === null ? null : [frozen, stoppage].filter((part) => part !== null).join(" ") || null;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full py-0.5 pl-[7px] pr-2", stopped ? "bg-white/[0.06]" : "bg-primary/[0.14]", className)}>
      <span className={cn("h-1.5 w-1.5 flex-shrink-0 rounded-full", stopped ? "bg-zinc-500" : "bg-primary motion-safe:animate-pulse")} aria-hidden="true" />
      <span data-testid="live-state" className={cn(KICKOFF_TIME, "leading-4", stopped && "text-zinc-400")}>
        {label}
        {detail !== null && <span className="font-medium tabular-nums text-zinc-400">{` ${detail}`}</span>}
      </span>
    </span>
  );
}
